import { updateCommitmentPublicServerCache, updateCommitmentServerCache } from "@data/commitment";
import {
  COMMITMENT_DASHBOARD_DATA_QUERY_KEY,
  COMMITMENT_GUESTS_QUERY_KEY,
  COMMITMENT_SERVERS_QUERY_KEY,
  GROUP_GUEST_QUERY_KEY,
  MEETING_ATTENDANCE_RECORDS_QUERY_KEY,
  MEETING_DATA_QUERY_KEY,
  queryClient
} from "@data/lib";
import { socket } from "@data/lib/socket";
import { CommitmentServer, useCommitmentStore } from "@mod/commitment";
import { useGroupStore } from "@mod/groups";
import { MeetingAttendanceRecord, Service } from "@mod/services";
import { useEffect, useState } from "react";
import { useParams } from "react-router-dom";

export enum emitParticipantEvents {
  UPDATE_PARTICIPANT = "update-participant",
  NEW_PARTICIPANT = "new-participant"
}

export enum emitCommitmentEvents {
  CREATE_SERVER = "commitment-create-server",
  UPDATE_SERVER = "commitment-update-server",
  UPDATE_SERVER_ATTENDANCE = "commitment-update-server-attendance",
  DELETE_SERVER = "commitment-delete-server"
}

export enum emitServicesEvents {
  UPDATE_ATTENDANCE = "update-attendance",
  UPDATE_SERVICES = "update-services",
  UPDATE_MEETING_SERVICES = "update-meeting-services"
}

export interface UpdateParticipantData {
  eventId: string;
  socketId: string;
}

export interface UpdateCommitmentServerData extends CommitmentServer {}
export interface DeleteCommitmentServerData {
  serverId: string;
}

export interface UpdateCommitmentServerAttendanceData {
  id: string;
  attendance: boolean;
}

export const useSocket = () => {
  const [isConnected, setIsConnected] = useState(socket.connected);
  const { eventSelected, groupSelected, viewAllGroups } = useGroupStore();
  const { commitmentSelected } = useCommitmentStore();
  const { commitmentHash, groupCode } = useParams();

  useEffect(() => {
    const onConnect = () => {
      setIsConnected(true);

      // If the connection was not recovered (e.g., client was disconnected for too long),
      // we invalidate the queries to refetch the fresh data and stay updated.
      if (!socket.recovered) {
        queryClient.invalidateQueries({ queryKey: [GROUP_GUEST_QUERY_KEY] });
        queryClient.invalidateQueries({ queryKey: [COMMITMENT_GUESTS_QUERY_KEY] });
        queryClient.invalidateQueries({ queryKey: [COMMITMENT_SERVERS_QUERY_KEY] });
        queryClient.invalidateQueries({ queryKey: [COMMITMENT_DASHBOARD_DATA_QUERY_KEY] });
        queryClient.invalidateQueries({ queryKey: [MEETING_ATTENDANCE_RECORDS_QUERY_KEY] });
        queryClient.invalidateQueries({ queryKey: [MEETING_DATA_QUERY_KEY] });
      }
    };

    const onDisconnect = () => {
      setIsConnected(false);
    };

    const onUpdateParticipant = (data: UpdateParticipantData) => {
      if (data.socketId === socket.id) return;
      if (eventSelected?.id === data.eventId) {
        queryClient.invalidateQueries({
          queryKey: [GROUP_GUEST_QUERY_KEY],
          type: "active"
        });
      }
    };

    const onCreateParticipant = (data: UpdateParticipantData) => {
      if (eventSelected?.id === data.eventId) {
        queryClient.invalidateQueries({
          queryKey: [GROUP_GUEST_QUERY_KEY],
          type: "active"
        });
      }
    };

    const onDeleteCommitmentServer = () => {
      queryClient.invalidateQueries({
        queryKey: [COMMITMENT_GUESTS_QUERY_KEY],
        type: "active"
      });

      queryClient.invalidateQueries({
        queryKey: [COMMITMENT_SERVERS_QUERY_KEY],
        type: "inactive"
      });

      queryClient.invalidateQueries({
        queryKey: [COMMITMENT_DASHBOARD_DATA_QUERY_KEY],
        type: "all"
      });
    };

    const onUpdateCommitmentServerAttendance = (data: UpdateCommitmentServerAttendanceData) => {
      const groupId = viewAllGroups ? "all" : (groupSelected?.id ?? "");

      updateCommitmentServerCache(
        {
          id: data.id,
          attendance: data.attendance
        },
        commitmentSelected?.id ?? "",
        groupId
      );

      updateCommitmentPublicServerCache(
        {
          id: data.id,
          attendance: data.attendance
        },
        {
          commitmentHash,
          groupCode
        }
      );
    };

    const onUpdateCommitmentServer = (_data: UpdateCommitmentServerData) => {
      // const groupId = viewAllGroups ? "all" : (groupSelected?.id ?? "");

      // if (data.commitmentId !== commitmentSelected?.id) return;

      // updateCommitmentServerCache(data, commitmentSelected?.id ?? "", groupId);

      queryClient.invalidateQueries({
        queryKey: [COMMITMENT_GUESTS_QUERY_KEY],
        type: "active"
      });

      queryClient.invalidateQueries({
        queryKey: [COMMITMENT_SERVERS_QUERY_KEY],
        type: "inactive"
      });

      queryClient.invalidateQueries({
        queryKey: [COMMITMENT_DASHBOARD_DATA_QUERY_KEY],
        type: "all"
      });
    };

    const onUpdateServicesAttendance = (data?: { record: MeetingAttendanceRecord }) => {
      if (data?.record) {
        queryClient.setQueriesData({ queryKey: [MEETING_ATTENDANCE_RECORDS_QUERY_KEY] }, (oldData: any) => {
          if (!oldData || !oldData.data) return oldData;

          const recordIndex = oldData.data.findIndex(
            (record: MeetingAttendanceRecord) => record.commitmentServerId === data.record.commitmentServerId
          );

          // if the record is not found, we should add it to the list.
          if (recordIndex === -1) {
            return { ...oldData, data: [...oldData.data, data.record] };
          }

          // if the record is found, we should update it in the list.
          const newData = oldData.data.map((record: MeetingAttendanceRecord) => {
            if (record.commitmentServerId === data.record.commitmentServerId) {
              return data.record;
            }
            return record;
          });

          return { ...oldData, data: newData };
        });
      } else {
        queryClient.invalidateQueries({ queryKey: [MEETING_ATTENDANCE_RECORDS_QUERY_KEY] });
      }
    };

    const onUpdateMeetingServices = (data?: { meetingServicesId: string; service?: Service }) => {
      if (data?.service && data?.meetingServicesId) {
        queryClient.setQueriesData({ queryKey: [MEETING_DATA_QUERY_KEY] }, (oldData: any) => {
          if (!oldData || !oldData.data || !oldData.data.nextGroupMeeting) return oldData;

          const newData = {
            ...oldData,
            data: { ...oldData.data }
          };

          const meetingServiceNextIndex = newData.data.nextGroupMeeting.findIndex(
            (ms: any) => ms.id === data.meetingServicesId
          );

          const meetingServicePreviousIndex = newData.data.previousGroupMeetingService.findIndex(
            (ms: any) => ms.id === data.meetingServicesId
          );

          if (meetingServiceNextIndex > -1) {
            newData.data.nextGroupMeeting[meetingServiceNextIndex] = {
              ...newData.data.nextGroupMeeting[meetingServiceNextIndex],
              services: [...newData.data.nextGroupMeeting[meetingServiceNextIndex].services]
            };

            const sIndex = newData.data.nextGroupMeeting[meetingServiceNextIndex].services.findIndex(
              (s: any) => s.groupServiceId === data.service?.groupServiceId
            );

            if (sIndex > -1) {
              newData.data.nextGroupMeeting[meetingServiceNextIndex].services[sIndex] = data.service;
            }
          }

          if (meetingServicePreviousIndex > -1) {
            newData.data.previousGroupMeetingService[meetingServicePreviousIndex] = {
              ...newData.data.previousGroupMeetingService[meetingServicePreviousIndex],
              services: [...newData.data.previousGroupMeetingService[meetingServicePreviousIndex].services]
            };

            const sIndex = newData.data.previousGroupMeetingService[meetingServicePreviousIndex].services.findIndex(
              (s: any) => s.groupServiceId === data.service?.groupServiceId
            );

            if (sIndex > -1) {
              newData.data.previousGroupMeetingService[meetingServicePreviousIndex].services[sIndex] = data.service;
            }
          }

          return newData;
        });
      } else {
        queryClient.invalidateQueries({ queryKey: [MEETING_DATA_QUERY_KEY] });
      }
    };

    socket.on("connect", onConnect);
    socket.on("disconnect", onDisconnect);
    socket.on(emitParticipantEvents.UPDATE_PARTICIPANT, onUpdateParticipant);
    socket.on(emitParticipantEvents.NEW_PARTICIPANT, onCreateParticipant);
    socket.on(emitCommitmentEvents.CREATE_SERVER, onUpdateCommitmentServer);
    socket.on(emitCommitmentEvents.UPDATE_SERVER, onUpdateCommitmentServer);
    socket.on(emitCommitmentEvents.UPDATE_SERVER_ATTENDANCE, onUpdateCommitmentServerAttendance);
    socket.on(emitCommitmentEvents.DELETE_SERVER, onDeleteCommitmentServer);
    socket.on(emitServicesEvents.UPDATE_ATTENDANCE, onUpdateServicesAttendance);
    socket.on(emitServicesEvents.UPDATE_MEETING_SERVICES, onUpdateMeetingServices);

    return () => {
      socket.off("connect", onConnect);
      socket.off("disconnect", onDisconnect);
      socket.off(emitParticipantEvents.UPDATE_PARTICIPANT, onUpdateParticipant);
      socket.off(emitParticipantEvents.NEW_PARTICIPANT, onCreateParticipant);
      socket.off(emitCommitmentEvents.CREATE_SERVER, onUpdateCommitmentServer);
      socket.off(emitCommitmentEvents.UPDATE_SERVER, onUpdateCommitmentServer);
      socket.off(emitCommitmentEvents.DELETE_SERVER, onDeleteCommitmentServer);
      socket.off(emitServicesEvents.UPDATE_ATTENDANCE, onUpdateServicesAttendance);
      socket.off(emitServicesEvents.UPDATE_MEETING_SERVICES, onUpdateMeetingServices);
    };
  }, []);

  return {
    isConnected
  };
};
